/**
 * Dev Auth Helper — utilities to improve dev UX with authentication
 *
 * Features:
 *   - Store dev credentials for quick re-auth after HMR reloads
 *   - One-click re-authentication without re-entering URL params
 *   - Automatic token refresh on development mode
 */

import { ACCESS_TOKEN_KEY } from "@be-miniapp/app-configs"
import { log } from "@be-miniapp/logging"

const DEV_CREDENTIALS_KEY = "be_dev_credentials"

interface DevCredentials {
  phoneNo: string
  partnerId?: number
  partnerSecret?: string
  fullname?: string
  ptr?: string
}

/**
 * Save current URL credentials to localStorage for auto-reuse
 * Call this after successful authentication to enable HMR persistence
 */
function saveDevCredentials(credentials: DevCredentials): void {
  try {
    localStorage.setItem(DEV_CREDENTIALS_KEY, JSON.stringify(credentials))
    log({
      category: "auth",
      level: "info",
      message: "Dev credentials saved for HMR persistence",
    })
  } catch (error) {
    log({
      category: "auth",
      level: "warning",
      message: "Failed to save dev credentials",
      data: { error },
    })
  }
}

/**
 * Retrieve saved dev credentials from localStorage
 */
function getDevCredentials(): DevCredentials | null {
  try {
    const stored = localStorage.getItem(DEV_CREDENTIALS_KEY)
    if (!stored) return null
    return JSON.parse(stored) as DevCredentials
  } catch {
    return null
  }
}

/**
 * Build auth URL with saved credentials for quick re-auth
 */
function buildDevAuthUrl(baseUrl: string = window.location.origin): string {
  const creds = getDevCredentials()
  if (!creds) return baseUrl

  const params = new URLSearchParams()
  params.set("phone_no", creds.phoneNo)
  if (creds.ptr) params.set("ptr", creds.ptr)
  if (creds.partnerId) params.set("partner_id", creds.partnerId.toString())
  if (creds.partnerSecret) params.set("partner_secret", creds.partnerSecret)
  if (creds.fullname) params.set("fullname", creds.fullname)

  return `${baseUrl}?${params.toString()}`
}

/**
 * Check if running in development mode
 */
function isDevMode(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "development"
}

/**
 * Get authentication status summary for debugging
 */
function getAuthDebugInfo(): {
  hasToken: boolean
  hasDevCreds: boolean
  devCreds: DevCredentials | null
} {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY)
  const creds = getDevCredentials()

  return {
    hasToken: !!token && token.length > 10,
    hasDevCreds: !!creds,
    devCreds: creds,
  }
}

export {
  saveDevCredentials,
  getDevCredentials,
  buildDevAuthUrl,
  isDevMode,
  getAuthDebugInfo,
  type DevCredentials,
}

// Development console helpers
if (typeof window !== "undefined" && isDevMode()) {
  // Expose dev helpers on window for console access
  ;(window as any).$auth = {
    saveCreds: saveDevCredentials,
    getCreds: getDevCredentials,
    buildUrl: buildDevAuthUrl,
    debug: getAuthDebugInfo,
    help: () => {
      // eslint-disable-next-line no-console
      console.log(`
🛠️ Dev Auth Helpers Available:

  $auth.saveCreds({ phoneNo: "0942641429", ptr: "uts" })  - Save credentials
  $auth.getCreds()                                        - Get saved credentials
  $auth.buildUrl()                                        - Build auth URL with saved creds
  $auth.debug()                                           - Show auth debug info

Quick re-auth after HMR:
  1. Your token is automatically restored from localStorage
  2. If token expired, use $auth.buildUrl() to get re-auth URL
  3. Or manually visit: http://appview.localhost:1355/?phone_no=0942641429&ptr=uts
      `)
    },
  }

  // Auto-show help on first load
  setTimeout(() => {
    // eslint-disable-next-line no-console
    console.log("💡 Auth dev helpers available. Type $auth.help() for details.")
  }, 1000)
}
